import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, CardBody, Badge, Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { get } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { PAYMENT_URL, PATIENT_URL } from 'helpers/url_helper';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import cookieHelper from 'helpers/getCookieData';
import { openReceiptPrintWindow } from 'pages/utils/printHelpers';
import {
  RiHandCoinLine,
  RiPrinterLine,
  RiCalendarEventLine,
  RiUserHeartLine,
  RiShieldCheckLine,
  RiCheckDoubleLine,
  RiWallet3Line,
  RiBankCardLine,
  RiQrCodeLine
} from 'react-icons/ri';

const ManagePayment = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState({ totalItems: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    Search: '',
  });

  const clinic = useMemo(() => {
    try {
      const raw = cookieHelper.getCookie('_c');
      return raw ? JSON.parse(atob(raw)) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await get(
        `${PAYMENT_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search || ''}`
      );
      if (res?.success || res?.items || Array.isArray(res?.body?.items)) {
        const items = res?.items || res?.body?.items || (Array.isArray(res) ? res : []);
        const total = res?.totalItems || res?.body?.totalItems || items.length;
        setRows({ totalItems: total, items });
      } else {
        setRows({ totalItems: 0, items: [] });
      }
    } catch (e) {
      console.error("Error fetching payments:", e);
      setRows({ totalItems: 0, items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [pagination]);

  const handlePrintReceipt = (payment) => {
    openReceiptPrintWindow(payment, clinic);
  };

  const handleTriggerPrint = () => {
    if (selectedReceipt) {
      openReceiptPrintWindow(selectedReceipt, clinic);
    } else {
      window.print();
    }
  };

  const getPaymentModeBadge = (mode) => {
    const m = (mode || 'Cash').toLowerCase();
    if (m.includes('upi') || m.includes('qr') || m.includes('gpay') || m.includes('phonepe')) {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
          📱 {mode || 'UPI / QR'}
        </span>
      );
    }
    if (m.includes('card') || m.includes('pos') || m.includes('debit') || m.includes('credit')) {
      return (
        <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
          💳 {mode || 'Card (POS)'}
        </span>
      );
    }
    if (m.includes('bank') || m.includes('neft') || m.includes('rtgs') || m.includes('transfer')) {
      return (
        <span className="badge bg-info-subtle text-info border border-info-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
          🏦 {mode || 'Bank Transfer'}
        </span>
      );
    }
    return (
      <span className="badge bg-secondary-subtle text-dark border border-secondary-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
        💵 {mode || 'Cash'}
      </span>
    );
  };

  const paymentColumns = [
    {
      dataField: 'id',
      text: '#',
      style: { width: '45px', textAlign: 'center', verticalAlign: 'middle' },
      formatter: (cell, row, rowIndex) => (
        <span className="badge bg-light text-muted border font-size-11 font-weight-bold">
          {(pagination?.page - 1) * (pagination?.limit || 10) + (rowIndex + 1)}
        </span>
      )
    },
    {
      dataField: 'receipt_no',
      text: 'Receipt #',
      style: { width: '130px', verticalAlign: 'middle' },
      formatter: (cell, row) => (
        <div>
          <span className="badge bg-success-subtle text-success border border-success-subtle font-monospace font-weight-bold font-size-11 px-2 py-1">
            {cell || `REC-${row.id}`}
          </span>
          <div className="font-size-11 text-muted mt-0.5 d-flex align-items-center gap-1">
            <RiCalendarEventLine className="text-muted" />
            {row.payment_date ? moment(row.payment_date).format('DD MMM YYYY') : moment(row.created_at).format('DD MMM YYYY')}
          </div>
        </div>
      )
    },
    {
      dataField: 'patient',
      text: 'Patient Details',
      style: { verticalAlign: 'middle' },
      formatter: (cell, row) => {
        const patient = row.patient || row.patients || {};
        const fullName = `${patient.title || ''} ${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
                         patient.full_name || row.patient_name || 'Patient';
        const mobile = patient.mobile || row.patient_mobile;
        const caseNo = patient.case_no;
        const initials = fullName.split(' ').filter(Boolean).map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'P';

        return (
          <div className="d-flex align-items-center gap-2.5 py-1">
            <div
              className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center font-weight-bold font-size-12 flex-shrink-0 shadow-2xs"
              style={{ width: '34px', height: '34px' }}
            >
              {initials}
            </div>
            <div>
              <div className="font-weight-bold text-dark font-size-13">
                {fullName}
              </div>
              <div className="d-flex align-items-center gap-1.5 mt-0.5 flex-wrap">
                {caseNo && (
                  <span className="badge bg-light text-dark border font-size-10 px-1.5 py-0.5">
                    #{caseNo}
                  </span>
                )}
                {mobile && (
                  <span className="text-muted font-size-11">📞 {mobile}</span>
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      dataField: 'billing_no',
      text: 'Settled Invoice',
      style: { verticalAlign: 'middle' },
      formatter: (cell) => {
        if (!cell || cell === 'ADVANCE') {
          return (
            <span className="badge bg-info-subtle text-info border border-info-subtle font-size-11 px-2 py-0.5">
              Advance Deposit
            </span>
          );
        }
        return (
          <span className="badge bg-light text-primary border font-monospace font-weight-bold font-size-11 px-2 py-0.5">
            #{cell}
          </span>
        );
      }
    },
    {
      dataField: 'payment_mode',
      text: 'Payment Mode',
      style: { verticalAlign: 'middle' },
      formatter: (cell) => getPaymentModeBadge(cell)
    },
    {
      dataField: 'amount',
      text: 'Amount Collected',
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerStyle: { textAlign: 'right' },
      formatter: (cell) => (
        <span className="font-weight-bold text-success font-size-14">
          ₹{parseFloat(cell || 0).toLocaleString('en-IN')}
        </span>
      )
    },
    {
      dataField: 'remark',
      text: 'Notes / Remarks',
      style: { verticalAlign: 'middle' },
      formatter: (cell) => (
        <span className="text-muted font-size-12 text-truncate d-inline-block" style={{ maxWidth: '180px' }} title={cell || ''}>
          {cell || 'Payment received'}
        </span>
      )
    },
    {
      dataField: 'actions',
      text: 'Receipt',
      isDummyField: true,
      style: { verticalAlign: 'middle', textAlign: 'center', width: '90px' },
      headerStyle: { textAlign: 'center', width: '90px' },
      formatter: (cell, row) => (
        <div className="d-flex align-items-center justify-content-center gap-1.5">
          <button
            type="button"
            className="btn btn-sm btn-soft-success d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold shadow-2xs"
            onClick={() => handlePrintReceipt(row)}
            title="View & Print Official Receipt"
          >
            <RiPrinterLine className="font-size-13" /> {t("Receipt")}
          </button>
        </div>
      )
    }
  ];

  return (
    <React.Fragment>
      <Datatables
        isSearch={true}
        columns={paymentColumns}
        showTableOnly={true}
        rowsLength={rows?.totalItems || 0}
        rows={rows?.items || []}
        keyField={'id'}
        title={t("All Payment Receipts & Collections")}
        isAdd={false}
        isTableHead={true}
        loading={loading}
        ssr={setPagination}
      />

      {/* Official Payment Receipt Modal */}
      <Modal isOpen={isReceiptModalOpen} toggle={() => setIsReceiptModalOpen(false)} size="md" centered>
        <ModalHeader toggle={() => setIsReceiptModalOpen(false)} className="border-bottom-0 pb-0">
          <span className="font-weight-bold font-size-16">{t("Official Money Receipt")}</span>
        </ModalHeader>
        <ModalBody className="p-4">
          {selectedReceipt && (
            <div id="printable-receipt-content" className="border p-3.5 rounded-3 bg-white">
              {/* Header Box */}
              <div className="text-center border-bottom pb-3 mb-3">
                <h5 className="font-weight-bold text-primary mb-1">{clinic?.clinic_name || "909 Dental OPD & Implant Clinic"}</h5>
                <p className="text-muted font-size-11 mb-1">
                  {clinic?.address || "Modern Dental Clinic, Oral Health & Implantology Center"}<br />
                  Phone: {clinic?.phone || "098220 11111"} • Email: {clinic?.email || "billing@oralstop.com"}
                </p>
                <Badge color="success" className="font-size-11 px-3 py-1 rounded-pill">
                  OFFICIAL PAYMENT RECEIPT
                </Badge>
              </div>

              {/* Receipt Meta */}
              <div className="d-flex justify-content-between font-size-12 mb-3 pb-2 border-bottom">
                <div>
                  <span className="text-muted">Receipt No:</span>{" "}
                  <strong className="font-monospace text-dark font-size-13">
                    #{selectedReceipt.receipt_no || `REC-${selectedReceipt.id}`}
                  </strong>
                </div>
                <div>
                  <span className="text-muted">Date:</span>{" "}
                  <strong>
                    {selectedReceipt.payment_date
                      ? moment(selectedReceipt.payment_date).format('DD MMM YYYY, hh:mm A')
                      : moment(selectedReceipt.created_at).format('DD MMM YYYY, hh:mm A')}
                  </strong>
                </div>
              </div>

              {/* Body Details */}
              <div className="font-size-12 mb-3">
                <div className="mb-2">
                  <span className="text-muted">Received with thanks from:</span>
                  <div className="font-weight-bold text-dark font-size-14 mt-0.5">
                    {selectedReceipt.patient?.full_name || selectedReceipt.patient_name || "Patient"}
                  </div>
                  <span className="text-muted font-size-11">
                    Case ID: #{selectedReceipt.patient?.case_no || "N/A"} • Phone: {selectedReceipt.patient?.mobile || "N/A"}
                  </span>
                </div>

                <div className="mb-2">
                  <span className="text-muted">Payment For / Invoice Reference:</span>
                  <div className="font-weight-bold text-primary font-size-12 mt-0.5">
                    {selectedReceipt.billing_no && selectedReceipt.billing_no !== 'ADVANCE'
                      ? `Settlement against Invoice #${selectedReceipt.billing_no}`
                      : 'Advance Treatment Deposit / Consultation'}
                  </div>
                </div>

                <div className="mb-3">
                  <span className="text-muted">Payment Mode:</span>
                  <div className="mt-1">
                    {getPaymentModeBadge(selectedReceipt.payment_mode)}
                  </div>
                </div>

                {/* Amount Highlight Box */}
                <div className="bg-success-subtle p-3 rounded-3 border border-success-subtle d-flex justify-content-between align-items-center">
                  <div>
                    <span className="text-success font-size-11 font-weight-bold text-uppercase d-block">Total Amount Received</span>
                    <strong className="font-size-11 text-muted">Status: Success &amp; Settled</strong>
                  </div>
                  <div className="font-weight-bold text-success font-size-20">
                    ₹{parseFloat(selectedReceipt.amount || 0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Signature stamp */}
              <div className="d-flex justify-content-between align-items-end pt-3 border-top mt-3 font-size-11 text-muted">
                <div>
                  <RiShieldCheckLine className="text-success me-1" /> Verified digital payment entry.
                </div>
                <div className="text-center">
                  <div style={{ height: '30px' }}></div>
                  <div className="border-top pt-1 font-weight-bold text-dark" style={{ minWidth: '120px' }}>
                    Received By
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
            <Button color="light" onClick={() => setIsReceiptModalOpen(false)}>
              {t("Close")}
            </Button>
            <Button color="success" onClick={handleTriggerPrint}>
              <RiPrinterLine className="me-1" /> {t("Print Receipt")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ManagePayment;
